import { html, attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { MoreInfoModalTemplate as template } from './more-info-modal.template';
import { MoreInfoModalStyles as styles } from './more-info-modal.styles';
import { CenteredModal } from '../../../centered-modal/centered-modal';
import {
  MORE_INFO_ITEM_TO_TITLE_VIEW,
  MORE_INFO_ITEMS,
} from '../../../../utils/enums/more-info.enum';

@customElement({
  name: 'more-info-modal',
  template,
  styles,
})
export class MoreInfoModal extends FASTElement {
  @observable moreInfoModal: CenteredModal;

  @attr show: boolean = false;
  @observable item: MORE_INFO_ITEMS;
  @observable title: string = 'More Info Title Placeholder';

  public openMoreInfoModal(data) {
    this.item = data.item;
    this.title = MORE_INFO_ITEM_TO_TITLE_VIEW.get(this.item);
    this.show = true;
  }

  public closeMoreInfoModal() {
    this.show = false;
  }

  public getContentFromItem() {
    switch (this.item) {
      case MORE_INFO_ITEMS.GENERATION:
        return html`
          <div class="content">
            <p>
              This graph shows the project’s historical and forecasted energy output. For
              Operational projects, observed data is used when available, while New Build projects
              rely on modeled data. Modeled data may also be shown for Operational projects if
              observed data is unavailable due to publication lags.
            </p>
            <p>
              The Net Capacity Factor indicates the efficiency of energy production relative to the
              project’s Nameplate Capacity. For example, a 100 MW project operating at 30 MW for an
              hour has a Net Capacity Factor of 30%.
            </p>
          </div>
        `;
      case MORE_INFO_ITEMS.SETTLEMENT_FORECAST:
        return html`
          <div class="content">
            <p>
              This graph models expected contract results under different scenarios (e.g., high gas
              prices, low gas prices) and weather outcomes. Settlement values are displayed from the
              perspective of the buyer.
            </p>
          </div>
        `;
      case MORE_INFO_ITEMS.FLOATING_PRICE:
        return html`
          <div class="content">
            <p>
              This graph displays forecasted generation-weighted market prices at the Settlement
              Point for the given Settlement Point Index (i.e., DA/RT) under different market and
              weather scenarios (e.g., high gas prices, low gas prices). Values are calculated from
              REsurety’s WeatherSmart forecast (
              <a
                href="https://resurety.com/weather-smart"
                target="_blank"
                rel="noopener noreferrer"
              >
                learn more here
              </a>
              ). The fixed price of the selected offer is shown in yellow.
            </p>
          </div>
        `;
      case MORE_INFO_ITEMS.IMPLIED_CARBON_PRICE:
        return html`
          <div class="content">
            <p>
              This graph displays the effective cost of carbon abatement expressed in $/MT CO2. The
              Implied Carbon Price (ICP) converts the expected settlement (in $/MWh) into a cost per
              ton of CO2 avoided, offering a measure of the financial efficiency of emissions
              reductions under the contract.
            </p>
            <p>
              A lower ICP reflects more cost-effective abatement, either through a lower settlement
              price or higher emissions displacement per unit of clean energy generated.
            </p>
            <p>
              The ICP is a financial indicator only; it does not imply additionality or assess the
              broader environmental impacts of emissions reductions.
            </p>
          </div>
        `;
      case MORE_INFO_ITEMS.LME_TOTAL_CARBON:
        return html`
          <div class="content">
            <p>
              Locational Marginal Emissions (LME) measure the emissions displaced (in millions of
              tons of carbon) by adding an additional MWh of renewable energy at the project’s
              specific location on the grid (
              <a
                href="https://resurety.com/lme-product-brief"
                target="_blank"
                rel="noopener noreferrer"
              >
                learn more here
              </a>
              ). Total Carbon Reduction is the sum of carbon displaced as a result of the selected
              project’s generation over the given period.
            </p>
          </div>
        `;
      case MORE_INFO_ITEMS.CARBON_FREE:
        return html`
          <div class="content">
            <p>
              This graph helps assess how well the project’s energy production aligns with an hourly
              matching (24/7) procurement strategy. The CFE % value shows how effectively the energy
              generated by the project can match real-time load-following consumption in the same
              hour and ISO/RTO.
            </p>
          </div>
        `;
      case MORE_INFO_ITEMS.NODAL_BASIS:
        return html`
          <div class="content">
            <p>
              This graph displays the historical spread in generation-weighted energy prices between
              the project’s Interconnection Point and the Settlement Point price. The Cumulative
              Nodal Settlement Basis shows historical price spreads to estimate how the selected
              offer would have performed historically.
            </p>
          </div>
        `;

      case MORE_INFO_ITEMS.SELLER_TRADING_ACTIVITY:
        return html`
          <div class="content">
            <p>
              Contracts that are under active negotiation in Structured Chat. Associated RFQs are
              nested under the related Contracts.
            </p>
          </div>
        `;
      case MORE_INFO_ITEMS.BUYER_TRADING_ACTIVITY:
        return html`
          <div class="content">
            <p>
              RFQs that are under active negotiation in Structured Chat. Associated Contracts are
              nested under the related RFQ.
            </p>
          </div>
        `;
      case MORE_INFO_ITEMS.ACTIVE_CONTRACTS:
        return html`
          <div class="content">
            <p>All active Contracts that are able to be traded on the platform.</p>
          </div>
        `;
      case MORE_INFO_ITEMS.INACTIVE_CONTRACTS:
        return html`
          <div class="content">
            <p>
              All inactive Contracts that are not able to be traded (e.g., deleted, delisted, or
              those awaiting Key Metrics).
            </p>
          </div>
        `;
      case MORE_INFO_ITEMS.ACTIVE_RFQS:
        return html`
          <div class="content">
            <p>All active RFQs that are able to be traded on the platform.</p>
          </div>
        `;
      case MORE_INFO_ITEMS.INACTIVE_RFQS:
        return html`
          <div class="content">
            <p>
              All inactive RFQs that are not able to be traded (e.g., deleted or delisted bids).
            </p>
          </div>
        `;
      case MORE_INFO_ITEMS.NEGOTIATIONS:
        return html`
          <div class="content">
            <p>Transactions that are under active negotiation in the contracting module.</p>
          </div>
        `;
      case MORE_INFO_ITEMS.EXECUTED:
        return html`
          <div class="content">
            <p>Transactions that have been successfully executed by both counterparties.</p>
          </div>
        `;
      case MORE_INFO_ITEMS.TERMINATED:
        return html`
          <div class="content">
            <p>Transactions that have been rejected or traded away.</p>
          </div>
        `;
      default:
        return html`
          <div class="content">
            <p>No data provided for this tooltip.</p>
          </div>
        `;
    }
  }
}
